import React, { useEffect, useMemo, useState } from 'react';
import useQueryGetUserDetail from '@/api/auth/getUser.ts';
import useIsMount from '@/util/hook/useIsMount.ts';
import { IMarker, IMarkerInfo, MarketTYPE } from '@/pages/dashboard/DashboardMapDefault.tsx';
import MarkerWindow from '@/pages/dashboard/MarkerWindow.tsx';
import { AlertStatus, DeviceStatus } from '@/api/user/getUsers.ts';
import { IUserBbox } from '@/api/user/getUsersBbox.ts';
import useQueryGetMe from '@/api/auth/getMe.ts';
import { useTranslation } from 'react-i18next';
import UnifiedMap from '@/component/map/unified/UnifiedMap';
import UnifiedMarker from '@/component/map/unified/UnifiedMarker';
import UnifiedCircle from '@/component/map/unified/UnifiedCircle';
import UnifiedInfoWindow from '@/component/map/unified/UnifiedInfoWindow';

import { useMapType } from '@/component/map/MapContext';
import { reverseGeocode } from '@/component/map/unified/UnifiedGeocoder';

interface IProps {
    className?: string;
    userID?: number;
    defaultOpenInfo?: boolean;
}

const UserDetailMap: React.FunctionComponent<IProps> = ({ className, userID = 0, defaultOpenInfo = false }) => {
    const { t } = useTranslation();
    const { data } = useQueryGetUserDetail(userID);
    const { isMount } = useIsMount();
    const [userInfo, setUserInfo] = useState<any>(null);
    const { data: me } = useQueryGetMe();
    const { mapType } = useMapType();

    const openUserInfo = async (info?: IMarker) => {
        if (!info || userInfo?.id === info?.id) {
            setUserInfo(undefined);
        } else {
            const { latitude = 0, longitude = 0 } = info?.location || {};

            if (latitude === 0 && longitude === 0) {
                const infoWithoutAddress: IMarkerInfo = {
                    ...info!,
                    address: 'Coordinates not available',
                };
                setUserInfo(infoWithoutAddress);
                return;
            }

            const address = await reverseGeocode(mapType, latitude, longitude);
            const infoWithAddress: IMarkerInfo = {
                ...info!,
                address,
            };
            setUserInfo(infoWithAddress);
        }
    };

    const getMarkerColor = (alertStatus: AlertStatus | undefined) => {
        let color = '#2966F4';
        switch (alertStatus) {
            case AlertStatus.EMERGENCY:
                color = '#FF003D';
                break;
            case AlertStatus.BIO:
                color = '#FF7D33';
                break;
            default:
                color = '#2966F4';
                break;
        }
        return color;
    };

    const marker = useMemo(() => {
        if (!data) return undefined;

        const userBboxData: IUserBbox = {
            id: data.id,
            name: data.name,
            location: data.location,
            todayAlert: data.alertStatus ?? AlertStatus.NONE,
            deviceStatus: data.device?.status ?? DeviceStatus.OFFLINE,
        };

        return {
            id: userID,
            deviceStatus: data?.device?.status,
            location: {
                latitude: data?.location?.latitude,
                longitude: data?.location?.longitude,
                accuracy: data?.location?.accuracy,
                timestamp: data?.location?.timestamp,
            },
            type: MarketTYPE.SINGLE,
            alertStatus: data?.alertStatus as AlertStatus ?? AlertStatus.NONE,
            users: [userBboxData],
        };
    }, [data]);

    useEffect(() => {
        if (defaultOpenInfo && data?.location) {
            openUserInfo(marker);
        }
    }, [defaultOpenInfo, data]);

    const mapOptions = {
        zoomControl: true,
        streetViewControl: false,
        fullscreenControl: false,
        gestureHandling: 'cooperative',
        scrollwheel: false,
        scrollWheelZoom: false
    };

    return (
        <>
            <div className={`${className} member-location-map relative`}>
                {isMount && data?.device ? (
                    <div
                        key={userID}
                        className="member-location-map__canvas"
                        style={{
                            width: '100%',
                            height: '100%',
                        }}
                    >
                        <UnifiedMap
                            center={{ lat: data?.location?.latitude || 0, lng: data?.location?.longitude || 0 }}
                            zoom={15}
                            mapContainerStyle={{ width: '100%', height: '100%' }}
                            options={mapOptions}
                        >
                            <UnifiedCircle
                                center={{ lat: data?.location?.latitude || 0, lng: data?.location?.longitude || 0 }}
                                radius={data?.location?.accuracy}
                                options={{
                                    fillColor: '#78bfffcc',
                                    strokeColor: '#78bfffcc',
                                    strokeWeight: 1,
                                }}
                            />
                            <UnifiedMarker
                                position={{ lat: data?.location?.latitude || 0, lng: data?.location?.longitude || 0 }}
                                icon={{
                                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                                        `
                                        <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50">
                                            <circle cx="25" cy="25" r="20" fill="${getMarkerColor(data?.alertStatus)}" stroke="white" stroke-width="3"/>
                                        </svg>
                                        `
                                    )}`,
                                    scaledSize: { width: 40, height: 40 },
                                }}
                                onClick={() => openUserInfo(marker)}
                            />
                            {userInfo && (
                                <UnifiedInfoWindow
                                    position={{ lat: userInfo?.location?.latitude || 0, lng: userInfo?.location?.longitude || 0 }}
                                    onCloseClick={() => setUserInfo(undefined)}
                                >
                                    <div style={{ fontFamily: "'Noto Sans KR', sans-serif", fontWeight: 400 }}>
                                        <MarkerWindow info={userInfo} isMasking={me?.email === 'dbd_cm@clomental.com'} />
                                    </div>
                                </UnifiedInfoWindow>
                            )}
                        </UnifiedMap>
                        <div className="member-location-map__status">
                            <span><i /> {t('users.detail.map.receiving')}</span>
                            <strong>{t('users.detail.map.accuracy', { value: Math.round(data?.location?.accuracy || 0) })}</strong>
                        </div>
                    </div>
                ) : (
                    <div className="member-location-map__empty">
                        <span>LOC</span>
                        <strong>{t('users.detail.map.unavailable')}</strong>
                        <p>{t('users.detail.map.connectDevicePrompt')}</p>
                    </div>
                )}
            </div>
        </>
    );
};

export default UserDetailMap;
